import * as THREE from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { SceneMeshData } from './types';
import defaultSkinTone from './defaultSkinTone';

function getMeshMaterial(mesh: THREE.Mesh): THREE.Material | undefined {
  const m = mesh.material;
  if (!m) return undefined;
  return Array.isArray(m) ? m[0] : m;
}

export function hideMeshMaterials(meshData: SceneMeshData) {
  meshData.scene.traverse((child) => {
    if (child instanceof THREE.Mesh && child.type === 'SkinnedMesh') {
      const material = getMeshMaterial(child);
      if (material?.name?.includes('Hide')) {
        material.visible = false;
      }
      if (
        material?.isMaterial &&
        child.geometry?.type === 'BufferGeometry'
      ) {
        // TODO: Subdivision modifier would go here. ThreeJS has poor support for it though
        // Tried to use https://github.com/stevinz/three-subdivide and it seemed to not work well for
        // modifying an existing mesh
      }
    }
    if ((child.name ?? '').endsWith('_RND')) {
      child.visible = false;
    }
  });
}

export function turnOnShadows(meshData: SceneMeshData) {
  meshData.scene.traverse((object: THREE.Object3D) => {
    object.castShadow = true;
    object.receiveShadow = true;
  });
}

export function disableFrustumCulled(meshData: SceneMeshData) {
  // Disable frustumCulled for children meant to be seen.
  // https://discourse.threejs.org/t/parts-of-glb-object-disappear-in-certain-angles-and-zoom/21295/4
  meshData.scene.traverse((child) => {
    if ('frustumCulled' in child) {
      const material =
        child instanceof THREE.Mesh ? getMeshMaterial(child) : undefined;
      if (child instanceof THREE.Mesh && material?.name?.includes('Hide')) {
        child.visible = false;
      } else {
        child.frustumCulled = false;
      }
    }
  });
}

export function showSkeleton(meshData: SceneMeshData) {
  const rootBone = meshData.scene.getObjectByName('root');
  if (rootBone) {
    const skeletonHelper = new THREE.SkeletonHelper(rootBone);
    (skeletonHelper.material as THREE.LineBasicMaterial).color =
      new THREE.Color(0x000000);
    meshData.scene.add(skeletonHelper);
  } else {
    console.warn('Could not find the root bone to show skeleton of');
  }
}

export function applySkinHex(skinHex?: string, charUploadID?: string) {
  const hexString = defaultSkinTone(skinHex, charUploadID);
  const hexNumber = hexStringToNumber(hexString);

  return (meshData: SceneMeshData) => {
    if (!meshData) return;
    meshData.scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.isMesh) {
        const material = getMeshMaterial(child);
        if (material && ['Body', 'Face'].includes(material.name)) {
          (material as THREE.MeshStandardMaterial).color.setHex(hexNumber);
          (material as THREE.MeshStandardMaterial).metalness = 0;
        }
      }
    });
  };
}

export function applyFaceExpression(faceExpression: string) {
  return (meshData: SceneMeshData) => {
    if (!meshData) return;
    const texturePath = `/images/static_faces_textures/${faceExpression}.png`;
    meshData.scene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.isMesh) {
        const material = getMeshMaterial(child);
        if (material?.name === 'Face') {
          // Axel doesn't have face expressions
          if (child.parent?.name?.includes('Axel')) {
            return;
          }
          new THREE.TextureLoader().load(texturePath, (tex) => {
            tex.flipY = false;
            (material as THREE.MeshStandardMaterial).map = tex;
            material.needsUpdate = true;
          });
        }
      }
    });
  };
}

export function applyShowSkeleton(meshData: SceneMeshData, showSkeleton: boolean) {
  if (!meshData) return;
  meshData.scene.traverse((child) => {
    const name = child.name ?? '';
    if (name.endsWith('_geo')) {
      child.visible = !showSkeleton;
    }
    if (name.endsWith('_skl')) {
      child.visible = showSkeleton;
    }
  });
}

export function applyShowRenderingCharacter(
  meshData: SceneMeshData,
  showRendering: boolean,
) {
  if (!meshData) return;
  meshData.scene.traverse((child) => {
    const name = child.name ?? '';
    if (name.endsWith('_geo')) {
      child.visible = !showRendering;
    }
    if (name.endsWith('_skl')) {
      child.visible = !showRendering;
    }
    if (name.endsWith('_RND')) {
      child.visible = showRendering;
    }
  });
}

function hexStringToNumber(hexString: string) {
  return parseInt(hexString.replace('#', ''), 16);
}
